import { useCallback, useEffect, useRef, useState } from "react";
import { FaArrowLeft, FaArrowRight, FaFish, FaUserShield, FaPhone, FaEnvelope, FaCheckCircle } from "react-icons/fa";
import "./CyberSecSlider.css";

// ─────────────────────────────────────────────────────────────────────────────
// BACKGROUND IMAGES — one per slide
// Using Unsplash Source URLs (free, no API key, CC0 licensed).
// Each is fixed at 1600×900 so browsers can cache a single copy.
// The CSS overlay darkens them to keep ZU brand colours dominant.
// ─────────────────────────────────────────────────────────────────────────────
// Slide 1 — phishing poster (custom image)
const BG_INTRO    = "/images/phishing-poster-week2.jpg";
// Slide 2 — smartphone / smishing
const BG_SMISHING = "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1600&q=80&fit=crop";
// Slide 3 — business person / authority
const BG_AUTHORITY = "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1600&q=80&fit=crop";
// Slide 4 — university campus / students
const BG_CASE1    = "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1600&q=80&fit=crop";
// Slide 5 — report / action
const BG_REPORT   = "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1600&q=80&fit=crop";

// How long each slide holds before auto-advancing (ms).
const SLIDE_DURATION = 7000;

// ─────────────────────────────────────────────────────────────────────────────
// WEEK 2 SLIDES  —  Phishing & Social Engineering
// Each slide is a focused, actionable awareness card.
// ─────────────────────────────────────────────────────────────────────────────
const CYBER_WEEK2_SLIDES = [
  // ── Intro / Campaign Hook ──────────────────────────────────────────────────
  {
    id: "phishing-intro",
    bgImage: BG_INTRO,
    week: "Week 2",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "Phishing & Social Engineering",
    headline: "Don't Get Tricked",
    body: "Attackers don't hack systems — they hack people. They weaponize urgency, authority, and curiosity to manipulate you into giving away credentials, personal information, or money.",
    icon: FaFish,
    accent: "shield",
    cta: { label: "Learn How to Spot Phishing", href: "/cyber-security-awareness-week2" },
    stat: { value: "91%", label: "of cyber attacks start with phishing" },
  },

  // ── Slide 2: Three Psychological Weapons ─────────────────────────────────
  {
    id: "phishing-weapons",
    bgImage: BG_SMISHING,
    week: "Week 2 · Psychology",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "The Three Psychological Weapons",
    headline: "How Attackers Manipulate You",
    body: "Attackers exploit human psychology instead of technical vulnerabilities. They weaponize emotions to bypass your critical thinking.",
    icon: FaUserShield,
    accent: "lock",
    tactics: [
      { type: "Urgency", desc: "Your account will be deactivated within 24 hours — Act now or lose access permanently" },
      { type: "Authority", desc: "IT support asking you to reset your password · Professor offering a research opportunity" },
      { type: "Curiosity", desc: "See your 2026 performance review · You have an unclaimed scholarship" },
    ],
  },

  // ── Slide 3: Multi-Channel Phishing ────────────────────────────────────────
  {
    id: "phishing-channels",
    bgImage: BG_AUTHORITY,
    week: "Week 2 · Attack Vectors",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "Beyond Email",
    headline: "Phishing Happens Everywhere",
    body: "Phishing doesn't only happen through email. Attackers use multiple channels to reach you where you're most vulnerable.",
    icon: FaPhone,
    accent: "key",
    channels: [
      { channel: "Smishing (SMS)", risk: "Text messages with malicious links — people reply quickly" },
      { channel: "Vishing (Voice)", risk: "Phone calls requesting credentials or payment" },
      { channel: "Social Media", risk: "Fake messages from friends or recruiters" },
      { channel: "Chat Apps", risk: "Impersonation in WhatsApp, Telegram, Slack" },
    ],
  },

  // ── Slide 4: Why Universities Are High-Value Targets ───────────────────────
  {
    id: "phishing-universities",
    bgImage: BG_CASE1,
    week: "Week 2 · Target",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "Why Universities?",
    headline: "Your @zetech.ac.ke Email Is Valuable",
    body: "A staff or student's @zetech.ac.ke email carries built-in credibility. Once an attacker controls a campus email account, they can send phishing emails to thousands of people from a trusted address.",
    icon: FaEnvelope,
    accent: "mobile",
    key: "A trusted sender address does not guarantee a trusted message.",
    cases: [
      { title: "Case 1", desc: "One student compromised → 4,800 people receive phishing emails → 40+ click the link" },
      { title: "Case 2", desc: "Fake IT verification form → attackers gain access → pose as professors offering fake jobs" },
      { title: "Case 3", desc: "IT support impersonation calls → guide victims to fake SSO login page → capture credentials" },
    ],
  },

  // ── Slide 5: Reporting Suspicious Emails ────────────────────────────────────
  {
    id: "phishing-reporting",
    bgImage: BG_REPORT,
    week: "Week 2 · Take Action",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "The Three Don'ts",
    headline: "How to Report Phishing",
    body: "Every report helps the security team stop ongoing attacks, warn other users, and identify attacker infrastructure. Reporting is not admitting a mistake — it's helping the entire community stay safer.",
    icon: FaCheckCircle,
    accent: "check",
    cta: { label: "Report to ICT", href: "mailto:itsupport@zetech.ac.ke?subject=Phishing%20Report" },
    donts: [
      "Don't click any links or attachments",
      "Don't reply to the sender (confirms your email is active)",
      "Don't forward it to classmates (spreads malicious links)",
    ],
    checklist: [
      "Select the suspicious email in Outlook",
      "Click Report → Report Phishing",
      "Or right-click → Report Phishing",
      "If you already clicked: Report immediately, change password, contact bank if financial info involved",
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

// Detect mobile viewport so we can limit the amount of content rendered
// inside the fixed-height hero. Content-heavy slides are trimmed on small screens.
function useMobileBreakpoint(px = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < px : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${px - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [px]);
  return isMobile;
}

export default function CyberSecWeek2Slider({ onCycleEnd, onRequestVideo }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const isMobile = useMobileBreakpoint(768);

  const onCycleEndRef = useRef(onCycleEnd);
  const onRequestVideoRef = useRef(onRequestVideo);
  useEffect(() => { onCycleEndRef.current = onCycleEnd; }, [onCycleEnd]);
  useEffect(() => { onRequestVideoRef.current = onRequestVideo; }, [onRequestVideo]);

  const hoverCapable = useRef(
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover)").matches
  );

  const reducedMotion = useRef(
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % CYBER_WEEK2_SLIDES.length);
  }, []);

  const prevSlide = useCallback(() => {
    if (current === 0 && onRequestVideoRef.current) {
      onRequestVideoRef.current();
      return;
    }
    setCurrent((prev) => (prev - 1 + CYBER_WEEK2_SLIDES.length) % CYBER_WEEK2_SLIDES.length);
  }, [current]);

  // Touch / swipe
  const touchStart = useRef(null);
  const handleTouchStart = (e) => { touchStart.current = e.touches?.[0]?.clientX ?? null; };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const delta = (e.changedTouches?.[0]?.clientX ?? touchStart.current) - touchStart.current;
    touchStart.current = null;
    if (Math.abs(delta) < 45) return;
    if (delta < 0) nextSlide();
    else prevSlide();
  };

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [nextSlide, prevSlide]);

  // Auto-advance
  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => {
      setCurrent((prev) => {
        if (prev >= CYBER_WEEK2_SLIDES.length - 1) return prev;
        return prev + 1;
      });
    }, SLIDE_DURATION);
    return () => clearInterval(timer);
  }, [paused]);

  // Finished last slide → hand off to HeroSlider
  useEffect(() => {
    if (paused || current < CYBER_WEEK2_SLIDES.length - 1) return undefined;
    const timer = setTimeout(() => {
      if (onCycleEndRef.current) onCycleEndRef.current();
    }, SLIDE_DURATION);
    return () => clearTimeout(timer);
  }, [current, paused]);

  const slide = CYBER_WEEK2_SLIDES[current];
  const Icon = slide.icon;

  return (
    <section
      className={`cyber-slider cyber-slider--${slide.accent}`}
      onMouseEnter={() => { if (hoverCapable.current) setPaused(true); }}
      onMouseLeave={() => { if (hoverCapable.current) setPaused(false); }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-roledescription="carousel"
      aria-label="Cybersecurity Awareness Month 2026 — Week 2"
      data-reduced-motion={reducedMotion.current ? "true" : "false"}
    >
      {/* Background — full-bleed photo + brand overlay + circuit pattern */}
      <div className="cyber-bg" aria-hidden="true">
        {/* The photo — fades in on mount via CSS, Ken-Burns-free (still image) */}
        <img
          key={slide.bgImage}
          src={slide.bgImage}
          alt=""
          className="cyber-bg-photo"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        {/* Brand-tinted gradient scrim over the photo */}
        <div className="cyber-bg-scrim" />
        {/* Subtle SVG circuit overlay — keeps brand feel */}
        <div className="cyber-bg-grid" />
        {/* Accent colour radial glow */}
        <div className="cyber-glow cyber-glow--1" />
        <div className="cyber-glow cyber-glow--2" />
      </div>

      {/* Binary / hex rain strip — decorative */}
      <div className="cyber-rain" aria-hidden="true">
        {["01", "FF", "A3", "7E", "C1", "0D", "B9", "22", "5A", "F4"].map((v, i) => (
          <span key={i} className="cyber-rain__char" style={{ "--i": i }}>
            {v}
          </span>
        ))}
      </div>

      {/* Main content */}
      <div className="cyber-overlay">
        <div className="cyber-wrapper">

          {/* LEFT: campaign identity */}
          <div className="cyber-identity">
            <div className="cyber-shield-wrap" aria-hidden="true">
              <Icon className="cyber-icon" />
            </div>
            <p className="cyber-campaign">{slide.theme}</p>
            <span className="cyber-week-badge">{slide.week}</span>
          </div>

          {/* RIGHT: slide content */}
          <div className="cyber-content">
            <span className="cyber-eyebrow">{slide.eyebrow}</span>
            <h1 className="cyber-headline">{slide.headline}</h1>

            {slide.body && <p className="cyber-body">{slide.body}</p>}

            {/* Psychological weapons */}
            {slide.tactics && (
              <ul className="cyber-tactics">
                {(isMobile ? slide.tactics.slice(0, 2) : slide.tactics).map((t) => (
                  <li key={t.type} className="cyber-tactic-item">
                    <strong className="cyber-tactic-type">{t.type}</strong>
                    <span className="cyber-tactic-desc">{t.desc}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Multi-channel phishing */}
            {slide.channels && (
              <ul className="cyber-channels">
                {(isMobile ? slide.channels.slice(0, 2) : slide.channels).map((c) => (
                  <li key={c.channel} className="cyber-channel-item">
                    <strong>{c.channel}</strong>
                    <span className="cyber-channel-risk">{c.risk}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* University cases */}
            {slide.cases && (
              <ul className="cyber-cases">
                {(isMobile ? slide.cases.slice(0, 2) : slide.cases).map((c) => (
                  <li key={c.title} className="cyber-case-item">
                    <strong>{c.title}</strong>
                    <span>{c.desc}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Key takeaway */}
            {slide.key && (
              <div className="cyber-key">
                <span className="cyber-key-text">{slide.key}</span>
              </div>
            )}

            {/* Three Don'ts */}
            {slide.donts && (
              <ul className="cyber-donts">
                {slide.donts.map((d, i) => (
                  <li key={i} className="cyber-dont-item">
                    <span className="cyber-dont-label">DON'T</span>
                    {d}
                  </li>
                ))}
              </ul>
            )}

            {/* Reporting checklist */}
            {slide.checklist && (
              <ol className="cyber-checklist">
                {(isMobile ? slide.checklist.slice(0, 3) : slide.checklist).map((item, i) => (
                  <li key={i} className="cyber-checklist-item">
                    <FaCheckCircle className="cyber-check-icon" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ol>
            )}

            {/* Stat callout */}
            {slide.stat && (
              <div className="cyber-stat">
                <span className="cyber-stat-value">{slide.stat.value}</span>
                <span className="cyber-stat-label">{slide.stat.label}</span>
              </div>
            )}

            {/* CTA */}
            {slide.cta && (
              <a
                href={slide.cta.href}
                className="cyber-cta"
                target={slide.cta.href.startsWith("http") ? "_blank" : undefined}
                rel={slide.cta.href.startsWith("http") ? "noopener noreferrer" : undefined}
              >
                {isMobile ? "Read Full Article →" : slide.cta.label}
              </a>
            )}
          </div>

        </div>
      </div>

      {/* Arrows */}
      <button
        className="hero-arrow1 hero-arrow-left1 cyber-arrow"
        onClick={prevSlide}
        aria-label="Previous slide"
      >
        <FaArrowLeft />
      </button>
      <button
        className="hero-arrow1 hero-arrow-right1 cyber-arrow"
        onClick={nextSlide}
        aria-label="Next slide"
      >
        <FaArrowRight />
      </button>

      {/* Indicators */}
      <div className="hero-indicators cyber-indicators" role="tablist" aria-label="Choose a cybersecurity awareness slide">
        {onRequestVideo && (
          <button
            type="button"
            className="indicator indicator--video"
            onClick={() => onRequestVideo()}
            aria-label="Play the intro video"
            title="Intro video"
          />
        )}
        {CYBER_WEEK2_SLIDES.map((s, i) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={current === i}
            className={`indicator ${current === i ? "active" : ""}`}
            onClick={() => setCurrent(i)}
            aria-label={`Cybersecurity slide: ${s.eyebrow}`}
            title={s.eyebrow}
          >
            {current === i && !paused && (
              <span className="indicator-progress" key={`cp-${i}`} style={{ animationDuration: `${SLIDE_DURATION}ms` }} />
            )}
          </button>
        ))}
      </div>

      {/* Screen-reader status */}
      <p className="hero-sr-status" aria-live="polite">
        {`Cybersecurity slide ${current + 1} of ${CYBER_WEEK2_SLIDES.length}: ${slide.headline}`}
      </p>
    </section>
  );
}
